/**
 * Hero Slider Front-end JavaScript
 * Ultra-léger, utilise CSS Scroll Snap + Vanilla JS
 */

class HeroSlider {
	constructor(element) {
		this.slider = element;
		this.container = element.querySelector('.ecp-hero-slider__container');
		this.track = element.querySelector('.ecp-hero-slider__track');
		this.slides = [...this.track.querySelectorAll('.ecp-hero-slide')];
		this.pagination = element.querySelector('.ecp-hero-slider__pagination');

		// Sortir le watermark du container qui scrolle horizontalement (scroll-snap) :
		// tout enfant position:absolute d'un conteneur en overflow scroll SUIT le scroll
		// interne, ce qui fait bouger le filigrane à chaque changement de slide.
		// En le remontant au niveau du wrapper .ecp-hero-slider (lui aussi position:relative
		// et overflow:hidden), il reste fixe pendant que les slides défilent.
		this.hoistWatermark();

		// Configuration depuis data-attributes
		this.config = {
			autoplay: this.slider.dataset.autoplay === 'true',
			autoplayDelay: parseInt(this.slider.dataset.autoplayDelay) || 5000,
			pauseOnHover: this.slider.dataset.pauseOnHover === 'true',
			loop: this.slider.dataset.loop === 'true',
		};

		this.currentIndex = 0;
		this.autoplayTimer = null;
		this.isHovered = false;
		this.isVisible = true;

		// Si moins de 2 slides, pas besoin d'initialiser
		if (this.slides.length < 2) {
			return;
		}

		this.init();
	}

	/**
	 * Déplace le filigrane (.ecp-hero-slider__watermark) hors du container qui
	 * scrolle, pour qu'il reste fixe pendant le défilement des slides.
	 * Idempotent : ne fait rien s'il n'y a pas de watermark ou s'il est déjà
	 * en dehors du container.
	 */
	hoistWatermark() {
		if (!this.container) return;
		const watermark = this.container.querySelector(':scope > .ecp-hero-slider__watermark');
		if (!watermark) return;
		// On l'insère juste après le container, toujours dans le wrapper .ecp-hero-slider
		// qui a lui aussi position:relative + overflow:hidden — clipping préservé.
		this.container.after(watermark);
	}

	init() {
		this.createPagination();
		this.setupIntersectionObserver();
		this.setupKeyboardNavigation();
		this.setupVisibilityChange();

		if (this.config.autoplay) {
			this.startAutoplay();
		}

		if (this.config.pauseOnHover) {
			this.setupHoverPause();
		}

		// Respecter prefers-reduced-motion
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.stopAutoplay();
		}
	}

	/**
	 * Créer les dots de pagination
	 */
	createPagination() {
		this.pagination.innerHTML = '';

		this.slides.forEach((slide, index) => {
			const dot = document.createElement('button');
			dot.className = 'ecp-hero-slider__dot';
			dot.setAttribute('role', 'tab');
			dot.setAttribute('aria-label', `Slide ${index + 1}`);
			dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

			if (index === 0) {
				dot.classList.add('ecp-hero-slider__dot--active');
				dot.setAttribute('aria-current', 'true');
			}

			dot.addEventListener('click', () => {
				this.goToSlide(index);
			});

			this.pagination.appendChild(dot);
		});
	}

	/**
	 * Setup Intersection Observer pour détecter la slide visible
	 */
	setupIntersectionObserver() {
		const options = {
			root: this.container,
			threshold: 0.5, // 50% de la slide visible
		};

		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const index = this.slides.indexOf(entry.target);
					if (index !== -1) {
						this.updateActiveDot(index);
						this.currentIndex = index;
					}
				}
			});
		}, options);

		this.slides.forEach((slide) => {
			this.observer.observe(slide);
		});
	}

	/**
	 * Mettre à jour le dot actif
	 */
	updateActiveDot(index) {
		const dots = this.pagination.querySelectorAll('.ecp-hero-slider__dot');

		dots.forEach((dot, i) => {
			if (i === index) {
				dot.classList.add('ecp-hero-slider__dot--active');
				dot.setAttribute('aria-selected', 'true');
				dot.setAttribute('aria-current', 'true');
			} else {
				dot.classList.remove('ecp-hero-slider__dot--active');
				dot.setAttribute('aria-selected', 'false');
				dot.removeAttribute('aria-current');
			}
		});
	}

	/**
	 * Aller à une slide spécifique
	 */
	goToSlide(index) {
		if (index < 0 || index >= this.slides.length) {
			return;
		}

		this.slides[index].scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'start',
		});

		this.currentIndex = index;
	}

	/**
	 * Slide suivante
	 */
	nextSlide() {
		let nextIndex = this.currentIndex + 1;

		if (nextIndex >= this.slides.length) {
			if (this.config.loop) {
				nextIndex = 0;
			} else {
				return; // Pas de loop, on s'arrête
			}
		}

		this.goToSlide(nextIndex);
	}

	/**
	 * Slide précédente
	 */
	prevSlide() {
		let prevIndex = this.currentIndex - 1;

		if (prevIndex < 0) {
			if (this.config.loop) {
				prevIndex = this.slides.length - 1;
			} else {
				return; // Pas de loop, on s'arrête
			}
		}

		this.goToSlide(prevIndex);
	}

	/**
	 * Démarrer l'autoplay
	 */
	startAutoplay() {
		if (this.autoplayTimer) {
			return;
		}

		this.autoplayTimer = setInterval(() => {
			if (!this.isHovered && this.isVisible) {
				this.nextSlide();
			}
		}, this.config.autoplayDelay);
	}

	/**
	 * Arrêter l'autoplay
	 */
	stopAutoplay() {
		if (this.autoplayTimer) {
			clearInterval(this.autoplayTimer);
			this.autoplayTimer = null;
		}
	}

	/**
	 * Setup pause au hover
	 */
	setupHoverPause() {
		this.slider.addEventListener('mouseenter', () => {
			this.isHovered = true;
		});

		this.slider.addEventListener('mouseleave', () => {
			this.isHovered = false;
		});
	}

	/**
	 * Setup navigation clavier
	 */
	setupKeyboardNavigation() {
		this.slider.addEventListener('keydown', (e) => {
			// Ne gérer que si le slider ou ses enfants ont le focus
			if (!this.slider.contains(document.activeElement)) {
				return;
			}

			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault();
					this.prevSlide();
					break;
				case 'ArrowRight':
					e.preventDefault();
					this.nextSlide();
					break;
				case 'Home':
					e.preventDefault();
					this.goToSlide(0);
					break;
				case 'End':
					e.preventDefault();
					this.goToSlide(this.slides.length - 1);
					break;
			}
		});

		// Rendre le slider focusable
		if (!this.slider.hasAttribute('tabindex')) {
			this.slider.setAttribute('tabindex', '0');
		}
	}

	/**
	 * Setup Page Visibility API pour pause quand l'onglet est caché
	 */
	setupVisibilityChange() {
		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				this.isVisible = false;
			} else {
				this.isVisible = true;
			}
		});
	}

	/**
	 * Détruire l'instance
	 */
	destroy() {
		this.stopAutoplay();

		if (this.observer) {
			this.observer.disconnect();
		}
	}
}

/**
 * Initialiser tous les sliders au chargement du DOM
 */
document.addEventListener('DOMContentLoaded', () => {
	const sliders = document.querySelectorAll('.ecp-hero-slider');

	sliders.forEach((slider) => {
		new HeroSlider(slider);
	});
});
